import { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { SophiaAvatar } from '../components/SophiaAvatar';
import { LanguageSelector } from '../components/LanguageSelector';
import { Button } from '../components/ui/button';
import { Mail, Lock, User, Loader2 } from 'lucide-react';

export default function RegisterPage() {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const { register, loginWithGoogle } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !password.trim()) return;
    
    if (password.length < 6) {
      setError(language === 'de' ? 'Passwort muss mindestens 6 Zeichen haben' : 'Password must be at least 6 characters');
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      await register(email, password, name);
      navigate('/');
    } catch (err) {
      console.error('Register error:', err);
      const msg = err.response?.data?.detail || (language === 'de' ? 'Registrierung fehlgeschlagen' : 'Registration failed');
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center px-6 relative overflow-hidden noise-overlay">
      {/* Background gradient orbs */}
      <div className="absolute top-[-20%] right-[-10%] w-[400px] h-[400px] bg-sophia-pink-500/8 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-[-15%] left-[-10%] w-[350px] h-[350px] bg-mystic-teal-500/8 rounded-full blur-3xl pointer-events-none" />

      {/* Language selector */}
      <motion.div
        className="absolute top-6 right-6 z-20"
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.5 }}
      >
        <LanguageSelector />
      </motion.div>

      <motion.div
        className="w-full max-w-sm space-y-8 relative z-10"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        {/* Sophia Avatar */}
        <motion.div
          className="flex flex-col items-center"
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <SophiaAvatar variant="pink" size="lg" />
          <h1 className="font-display text-2xl font-bold mt-4 text-foreground">
            {language === 'de' ? 'Willkommen!' : 'Join Sophia!'}
          </h1>
          <p className="text-muted-foreground font-body text-sm mt-1">
            {language === 'de' ? 'Erstelle dein Konto, Liebling' : 'Create your account, darling'}
          </p>
        </motion.div>

        {/* Register Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="relative">
            <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <input
              data-testid="register-name-input"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={language === 'de' ? 'Name' : 'Name'}
              className="w-full h-12 pl-12 pr-4 rounded-xl bg-card border border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-sophia-pink-500/50 font-body"
              required
            />
          </div>

          <div className="relative">
            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <input
              data-testid="register-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email"
              className="w-full h-12 pl-12 pr-4 rounded-xl bg-card border border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-sophia-pink-500/50 font-body"
              required
            />
          </div>

          <div className="relative">
            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <input
              data-testid="register-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={language === 'de' ? 'Passwort (mind. 6 Zeichen)' : 'Password (min. 6 characters)'}
              className="w-full h-12 pl-12 pr-4 rounded-xl bg-card border border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-sophia-pink-500/50 font-body"
              required
            />
          </div>

          {error && (
            <motion.p
              data-testid="register-error"
              className="text-red-500 text-sm font-body text-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              {error}
            </motion.p>
          )}

          <Button
            data-testid="register-submit-btn"
            type="submit"
            disabled={isLoading}
            className="w-full h-12 rounded-xl bg-sophia-pink-500 hover:bg-sophia-pink-700 text-white font-display text-lg shadow-lg"
          >
            {isLoading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              language === 'de' ? 'Registrieren' : 'Create Account'
            )}
          </Button>
        </form>

        {/* Divider */}
        <div className="flex items-center gap-4">
          <div className="flex-1 h-px bg-border" />
          <span className="text-muted-foreground text-sm font-body">
            {language === 'de' ? 'oder' : 'or'}
          </span>
          <div className="flex-1 h-px bg-border" />
        </div>

        {/* Google Login */}
        <Button
          data-testid="register-google-btn"
          type="button"
          onClick={loginWithGoogle}
          variant="outline"
          className="w-full h-12 rounded-xl border-border hover:bg-card font-body gap-2"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          {language === 'de' ? 'Mit Google registrieren' : 'Sign up with Google'}
        </Button>

        {/* Login link */}
        <p className="text-center text-sm font-body text-muted-foreground">
          {language === 'de' ? 'Bereits ein Konto?' : 'Already have an account?'}{' '}
          <Link
            to="/login"
            data-testid="register-login-link"
            className="text-sophia-pink-500 hover:text-sophia-pink-700 font-medium"
          >
            {language === 'de' ? 'Anmelden' : 'Sign in'}
          </Link>
        </p>
      </motion.div>
    </div>
  );
}
